import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import produce from 'immer';
import { getWeeksInMonth, getDayEventPropsMap, getEventIds, getLondonDateTime, isSameDate, isSelectable, getInitialDate, } from './utils';
import styles from './EventCalendar.module.scss';
import { Button } from '..';
import { MemoizedCalendarWrapper } from './EventCalendarWrapper';
import { EventCalendarLoader } from './EventCalendarLoader';
import EventCalendarDayStyles from './EventCalendarDay.module.scss';
import { useScreenWidth } from '../../utils/useScreenWidth';
const monthsDisplayNum = 24;
export const EventCalendar = ({ local, dateSelection, eventData, onSelect: onSelectCallback, onAddEventClick, plusIconRef, onKeyClick, monthlySummary, initialDate = null, scrollToSelection = false, onScroll, }) => {
    const monthsWrapperRef = useRef(null);
    const monthsRefs = useRef([]);
    const initialDateTime = getInitialDate(initialDate);
    const [loading, setLoading] = useState(true);
    const [monthLabel, setMonthLabel] = useState(initialDateTime.startOf('month').toFormat('MMM yyyy'));
    const [months, setMonths] = useState([]);
    const [month, setMonth] = useState(initialDateTime.startOf('month'));
    const [dayEventPropsMap, setDayEventMap] = useState({});
    // inital scroll set / today scroll set
    const todayDayElement = useRef(document.querySelector(`.${EventCalendarDayStyles.calendarDayToday}`));
    const startOfRangeElement = useRef(document.querySelector(`.${EventCalendarDayStyles.calendarDayStartOfRange}`));
    useEffect(() => {
        var _a, _b, _c, _d;
        if (scrollToSelection) {
            if (!startOfRangeElement.current && monthsWrapperRef.current) {
                startOfRangeElement.current = document.querySelector(`.${EventCalendarDayStyles.calendarDayStartOfRange}`);
                if (startOfRangeElement.current) {
                    monthsWrapperRef.current.scrollTop = (_b = (_a = startOfRangeElement.current) === null || _a === void 0 ? void 0 : _a.offsetParent) === null || _b === void 0 ? void 0 : _b.offsetTop;
                }
            }
        }
        else {
            if (!todayDayElement.current && monthsWrapperRef.current) {
                todayDayElement.current = document.querySelector(`.${EventCalendarDayStyles.calendarDayToday}`);
                if (todayDayElement.current) {
                    monthsWrapperRef.current.scrollTop = (_d = (_c = todayDayElement.current) === null || _c === void 0 ? void 0 : _c.offsetParent) === null || _d === void 0 ? void 0 : _d.offsetTop;
                }
            }
        }
    });
    const isMobileView = useScreenWidth(960);
    const localSelection = useMemo(() => {
        var _a, _b;
        return ({
            startDate: (_a = dateSelection.startDate) === null || _a === void 0 ? void 0 : _a.toLocal(),
            endDate: (_b = dateSelection.endDate) === null || _b === void 0 ? void 0 : _b.toLocal(),
        });
    }, [dateSelection]);
    useEffect(() => {
        monthsRefs.current = monthsRefs.current.slice(0, monthsDisplayNum);
    }, []);
    useEffect(() => {
        const prevYear = month.minus({ year: 1 });
        const nextYear = month.plus({ year: 1 });
        const months = [];
        for (let currMonth = prevYear; currMonth < nextYear; currMonth = currMonth.plus({ month: 1 })) {
            months.push({
                weeks: getWeeksInMonth(currMonth),
            });
        }
        setMonths(months);
        setLoading(false);
    }, [month]);
    useEffect(() => {
        if (eventData) {
            setDayEventMap(getDayEventPropsMap(eventData));
        }
    }, [eventData]);
    const setSelection = useCallback((startDate, endDate) => {
        const zoneSelection = {
            startDate: local ? startDate : getLondonDateTime(startDate),
            endDate: local ? endDate : getLondonDateTime(endDate),
        };
        if (eventData) {
            const selectedEventIds = getEventIds(zoneSelection, eventData);
            onSelectCallback(zoneSelection, selectedEventIds);
        }
        else {
            onSelectCallback(zoneSelection, null);
        }
    }, [eventData, local, onSelectCallback]);
    const handleRefPopulate = useCallback((el, monthIndex) => {
        if (el)
            monthsRefs.current[monthIndex] = el;
    }, []);
    const onSelect = useCallback((date) => {
        if (!isSelectable(date)) {
            return;
        }
        // "init" select
        if (!localSelection.startDate) {
            setSelection(date, date);
            return;
        }
        // do not set end when an end date is selected before a start date
        if (isSameDate(localSelection.startDate, localSelection.endDate) &&
            date > localSelection.startDate) {
            const nextSelectionState = produce(localSelection, (draftState) => {
                draftState.endDate = date;
            });
            setSelection(nextSelectionState.startDate, nextSelectionState.endDate);
            return;
        }
        const nextSelectionState = produce(localSelection, (draftState) => {
            draftState.startDate = date;
            draftState.endDate = date;
        });
        setSelection(nextSelectionState.startDate, nextSelectionState.endDate);
    }, [localSelection, setSelection]);
    const nextMonths = () => setMonth((prev) => prev.plus({ months: 3 }));
    const prevMonths = () => setMonth((prev) => prev.minus({ months: 3 }));
    const scrollObserver = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) {
            const monthElement = entry.target;
            const offset = monthElement.offsetTop;
            const container = entry.target.parentElement;
            container.scrollTop = offset;
            scrollObserver.disconnect();
        }
    });
    const overflowAnchorUnsupported = !CSS.supports('overflow-anchor', 'auto');
    const handleOnScroll = (event) => {
        var _a, _b;
        const outerHeight = event.currentTarget.getBoundingClientRect().height;
        const outerScrollTop = event.currentTarget.scrollTop;
        const outerScrollHeight = event.currentTarget.scrollHeight;
        const monthTopPadding = 60;
        const visibleMonths = monthsRefs.current.filter((ref) => {
            if (ref) {
                const rect = ref.getBoundingClientRect();
                const exactTop = ref.offsetTop - monthTopPadding;
                const exactBottom = exactTop + rect.height - monthTopPadding;
                const outerBottomScrollInterval = outerScrollTop + rect.height;
                return exactTop <= outerBottomScrollInterval && exactBottom >= outerScrollTop;
            }
            return false;
        });
        if (visibleMonths.length) {
            let monthLabel = ((_a = visibleMonths[0]) === null || _a === void 0 ? void 0 : _a.id) || '';
            if (visibleMonths.length >= 2) {
                monthLabel = monthLabel === null || monthLabel === void 0 ? void 0 : monthLabel.concat(` - ${(_b = visibleMonths[1]) === null || _b === void 0 ? void 0 : _b.id}`);
            }
            setMonthLabel(monthLabel);
        }
        if (!loading) {
            if (onScroll)
                onScroll();
            if (outerScrollTop === 0) {
                event.currentTarget.scrollTop = 1;
                prevMonths();
            }
            if (outerScrollHeight - outerScrollTop === outerHeight) {
                if (overflowAnchorUnsupported) {
                    const finalMonth = visibleMonths.length >= 2 ? visibleMonths[1] : visibleMonths[0];
                    scrollObserver.observe(finalMonth);
                }
                nextMonths();
            }
        }
    };
    const handleTodayClick = () => {
        var _a;
        if (!monthsWrapperRef.current) {
            return;
        }
        if (!todayDayElement.current) {
            todayDayElement.current = document.querySelector(`.${EventCalendarDayStyles.calendarDayToday}`);
        }
        if (todayDayElement.current) {
            monthsWrapperRef.current.scrollTop = ((_a = todayDayElement.current) === null || _a === void 0 ? void 0 : _a.offsetParent).offsetTop;
        }
    };
    return (<div className={styles.eventCalendar}>
      <div className={styles.eventCalendarNavigation}>
        <span className={styles.monthLabel}>{monthLabel}</span>
        <div className={styles.eventCalendarNavigationActions}>
          <Button onClick={handleTodayClick} variant='text'>
            Today
          </Button>
          <Button onClick={onKeyClick} variant='text'>
            Key
          </Button>
          <div ref={plusIconRef}>
            <Button onClick={onAddEventClick} variant='text'>
              <img className={styles.plusIcon} src='https://www.haven.com/assets/owners/shared/plus.svg' alt='plus' role='presentation'/>
              {!isMobileView && !(dateSelection === null || dateSelection === void 0 ? void 0 : dateSelection.startDate) && 'Add booking'}
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.eventCalendarDayLabels}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((weekLabel, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`${weekLabel}-${i}`}>
            <span>{weekLabel}</span>
          </div>))}
      </div>

      <div ref={monthsWrapperRef} className={styles.eventCalendarMonthsWrapper} onScroll={handleOnScroll}>
        {loading ? (<EventCalendarLoader />) : (<MemoizedCalendarWrapper months={months} localSelection={localSelection} dayEventPropsMap={dayEventPropsMap} onSelect={onSelect} handleRefPopulate={handleRefPopulate} monthlySummary={monthlySummary}/>)}
      </div>
    </div>);
};
